import { useFullRelease } from "@/common/hooks_useFullRelease";
import { DecisionFlowGraphContext } from "@/components/flow/DecisionFlowGraph";
import type { FlowStep, FlowTrigger } from "@/components/flow/DecisionFlowUtils";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import { shownNode } from "@/components/GraphVisualisation/decisionGraphView";
import GraphNode from "@imminently/decisively-ui/GraphNode";
import Icon from "@imminently/decisively-ui/Icon";
import { Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import type { TriggerType } from "@packages/commons";
import { Handle, Position } from "@xyflow/react";
import cn from "clsx";
import type React from "react";
import { useContext } from "react";
import { getAttributeIconAsComponentType } from "../../../common";

import { ControlIcons } from "../../../pages/models/release/Interview/Designer/components/ControlIcon";
import type { GraphViewNodeProps } from "../../GraphView/GraphView";
import SelectDecisive, { type RequiredDecisiveValue } from "../../select/SelectDecisive";
import stl from "./DebuggerGraphNode.module.scss";

interface TriggerNodeProps<N = any> extends GraphViewNodeProps<N> { }

{
  /** TODO probably automate picking out the values and the attribs inside here... */
}
const ConditionText = (props: React.PropsWithChildren<{}>) => {
  return (
    <Typography
      variant="caption"
      className={cn(stl.condition_text, stl.mb_0_5)}
    >
      &raquo; {props.children}
    </Typography>
  );
};

const SeparatorBar = (props: {}) => {
  return (
    <div className={stl.separator_bar}>
      <span className={stl.separator_bar_text}>+</span>
    </div>
  );
};

interface TriggerConditionsProps {
  trigger: FlowTrigger;
  id: string;
  triggerInputId: string;
}

const TriggerConditions = (props: TriggerConditionsProps) => {
  const { trigger, id, triggerInputId } = props;

  return (
    <div className={cn(stl.trigger_section, stl.mb_0_5)}>
      <Handle
        type="target"
        position={Position.Left}
        id={triggerInputId}
        itemID={triggerInputId}
      />

      <GraphNode.ColLhs className={stl.col_lhs}>
        <Icon
          name="trigger"
          style={{
            marginLeft: "-7px",
          }}
        />
      </GraphNode.ColLhs>

      <GraphNode.ColRhs className={stl.col_rhs}>
        <Typography className={cn(stl.trigger_txt)}>When the conditions are met</Typography>
      </GraphNode.ColRhs>
    </div>
  );
};

const TRIGGER_TYPE_ICONS: Partial<Record<TriggerType, React.ComponentType<any>>> = {
  task: (p: any) => <Icon name="tasks" {...p} />,
  notification: (p: any) => <Icon name="notification" {...p} />,
  comms: (p: any) => (
    <Icon
      name="email"
      {...p}
    />
  ),
  enrichment: (p: any) => (
    <Icon
      name="open-in-new"
      {...p}
    />
  ),
  integration: (p: any) => (
    <Icon
      name="open-in-new"
      {...p}
    />
  ),
  storage: (p: any) => (
    <Icon
      name="open-in-new"
      {...p}
    />
  ),
};

const TRIGGER_TYPE_NAMES: Partial<Record<TriggerType, string>> = {
  task: "Task",
  notification: "Notification",
  comms: "Communication",
  enrichment: "Enrichment",
  integration: "Integration",
  storage: "Storage",
  contact: "Contact",
};

export interface OutcomeSectionProps {
  trigger: FlowTrigger;
  onRequiredDecisiveChange?: (step: FlowStep, trigger: FlowTrigger, value: RequiredDecisiveValue) => void;
  step: FlowStep;
  release: any;
}

const OutcomeSection = (props: OutcomeSectionProps) => {
  const { trigger, step, onRequiredDecisiveChange, release } = props;
  const { actions, required, decisive } = trigger;
  const parsedGraph = useParsedGraph();

  const actionsWithOutcome = actions.filter((action: any) => action.outcome);
  if (!actionsWithOutcome.length) {
    return null;
  }

  return (
    <div className={stl.requirement_section}>
      <div className={stl.wrap_select_decisive}>
        <SelectDecisive
          complex={trigger.complex}
          value={{ decisive, required }}
          onChange={(value) => onRequiredDecisiveChange?.(step, trigger, value)}
        />
      </div>

      <Typography
        className={cn(stl.subtitle_txt)}
        style={{ marginBottom: "0.1rem" }}
      >
        Task outcome:
      </Typography>

      <div className={stl.outcome}>
        {(actionsWithOutcome || []).map((action: any) => {
          if (!action.outcome) {
            return null;
          }
          const node = shownNode(parsedGraph, null, action.outcome);
          if (!node) {
            console.warn("No node found for action", action);
            return null;
          }

          const responseMappingId = action?.assignmentIntegration?.responseMapping;
          const responseMappingName = responseMappingId
            ? release?.mapping?.find((m: any) => m.id === responseMappingId)?.name
            : undefined;
          const IconComponent = TRIGGER_TYPE_ICONS[action.type as TriggerType] || ControlIcons.unassigned!;

          return (
            <div
              key={node.id}
              className={cn(stl.trigger_section, stl.mb_0_5)}
              style={{ position: "relative" }}
            >
              <GraphNode.ColRhs
                className={stl.col_rhs}
                style={{ flex: 1 }}
              >
                <Typography className={cn(stl.outcome_txt)}>
                  {node.description || ""}
                  {responseMappingName ? ` · Uses response mapping: ${responseMappingName}` : ""}
                </Typography>
              </GraphNode.ColRhs>
              {responseMappingId ? (
                <GraphNode.ColLhs
                  className={stl.col_lhs}
                  style={{ marginRight: "0", marginLeft: "0.5rem" }}
                >
                  <IconComponent />
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`mapping-${responseMappingId}`}
                  />
                </GraphNode.ColLhs>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TriggerNode = (props: TriggerNodeProps) => {
  const decisionFlowGraphState = useContext(DecisionFlowGraphContext);
  const release = useFullRelease();

  const { isDragging, data, ...otherProps } = props;

  const stylingClass = "source"; // "intermediate" | "goal" | "source" | "byValue"

  const parsedGraph = useParsedGraph();
  const step = data.node as FlowStep;
  const containmentNode = shownNode(parsedGraph, null, step.containment);

  const renderContainmentTypeIcon = () => {
    const nodeType = containmentNode?.type;
    const IconComponent_ = (ControlIcons as any)[nodeType] || ControlIcons.unassigned!;
    const IconComponent = getAttributeIconAsComponentType((nodeType || "") as any, IconComponent_);

    return (
      <GraphNode.TypeIcon className={cn(stl.type_icon, stl[stylingClass])}>
        <IconComponent />
      </GraphNode.TypeIcon>
    );
  };

  return (
    <GraphNode className={cn(stl.container, stl[stylingClass], { [stl.dragging]: isDragging })}>
      {/* body: each trigger section */}
      {data.node.triggers.map((trigger) => {
        const inputNode = shownNode(parsedGraph, null, trigger.input);
        const actionsWithOutcome = trigger.actions.filter((action: any) => action.outcome);

        return (
          <div
            key={trigger.input}
            className={stl.decision_flow_section}
            {...otherProps}
          >
            <div className={stl.decision_flow_inner}>
              {/* Header: trigger input node (moved from inside "When the following is true") */}
              <GraphNode.Header
                className={cn(stl.col_container, stl[stylingClass], stl.flush_top)}
                style={{ position: "relative" }}
              >
                <GraphNode.ColLhs className={stl.col_lhs}>
                  <GraphNode.TypeIcon className={cn(stl.type_icon, stl[stylingClass])}>
                    <ControlIcons.boolean />
                  </GraphNode.TypeIcon>
                </GraphNode.ColLhs>
                <GraphNode.ColRhs className={stl.col_rhs}>
                  {inputNode ? inputNode.description || "" : ""}
                </GraphNode.ColRhs>
              </GraphNode.Header>

              {/* Conditions row: lightning bolt + "When the conditions are met" */}
              <div className={stl.conditions_spacing}>
                <TriggerConditions
                  trigger={trigger}
                  id={step.id}
                  triggerInputId={trigger.input || step.id}
                />
              </div>

              <div className={stl.actions_section}>
                {actionsWithOutcome?.length ? (
                  <>
                    <ConditionText>And these actions are completed</ConditionText>
                    <OutcomeSection
                      step={step}
                      onRequiredDecisiveChange={decisionFlowGraphState.setRequiredDecisive}
                      trigger={trigger}
                      release={release}
                    />
                  </>
                ) : (
                  <>
                    <ConditionText>The following will be triggered</ConditionText>
                    {trigger.actions.map((action: any, index) => {
                      const responseMappingId = action?.assignmentIntegration?.responseMapping;
                      const responseMappingName = responseMappingId
                        ? release?.mapping?.find((m: any) => m.id === responseMappingId)?.name
                        : undefined;
                      let IconComponent = TRIGGER_TYPE_ICONS[action.type as TriggerType] || ControlIcons.unassigned!;
                      if (action.type === "comms") {
                        IconComponent =
                          action.method === "email"
                            ? (p: any) => (
                              <Icon
                                name="email"
                                {...p}
                              />
                            )
                            : (p: any) => (
                              <Icon
                                name="phone"
                                {...p}
                              />
                            );
                      }
                      const isTaskAction = action?.type === "task";
                      const taskHandleId = isTaskAction ? `task-${step.containment}-${trigger.input}-${index}` : null;
                      return (
                        <div
                          key={index}
                          className={cn(stl.trigger_section, stl.mb_0_5)}
                          style={{ position: "relative", marginLeft: 0 }}
                        >
                          <GraphNode.ColRhs
                            className={stl.col_rhs}
                            style={{ flex: 1 }}
                          >
                            <Typography className={cn(stl.trigger_txt)}>
                              {TRIGGER_TYPE_NAMES[action.type as TriggerType]}:
                              {action.title && action.title.length > 40 ? (
                                <Tooltip
                                  title={action.title}
                                  placement="top"
                                >
                                  <span>{`${action.title.slice(0, 40)}...`}</span>
                                </Tooltip>
                              ) : (
                                <span>{action.title}</span>
                              )}
                            </Typography>
                          </GraphNode.ColRhs>
                          <GraphNode.ColLhs
                            className={stl.col_lhs}
                            style={{ marginRight: "0", marginLeft: "0.5rem", padding: "10px" }}
                          >
                            <IconComponent />
                            {taskHandleId ? (
                              <Handle
                                type="source"
                                position={Position.Right}
                                id={taskHandleId}
                              />
                            ) : null}
                          </GraphNode.ColLhs>
                        </div>
                      );
                    })}
                  </>
                )}

                <SeparatorBar />

                {/* "And updates" section with containment node at bottom */}
                <ConditionText>And helps satisfy</ConditionText>
              </div>
              <GraphNode.Header
                className={cn(stl.col_container, stl[stylingClass], stl.flush_bottom)}
                style={{ position: "relative" }}
              >
                <GraphNode.ColLhs className={stl.col_lhs}>{renderContainmentTypeIcon()}</GraphNode.ColLhs>
                <GraphNode.ColRhs className={stl.col_rhs}>
                  {containmentNode ? containmentNode.description || "" : ""}
                </GraphNode.ColRhs>
                <Handle
                  type={"source"}
                  position={Position.Right}
                  itemID={containmentNode?.id}
                />
              </GraphNode.Header>
            </div>
          </div>
        );
      })}
    </GraphNode>
  );
};

export default TriggerNode;
